/**
 * Shared formatting for subscription "Data Storage" from API byte counts.
 * Uses binary units (1024² = MB, 102³ = GB) so sub-gigabyte plans show meaningful values
 * instead of rounding to 0.0 GB / 0.1 GB.
 */

const BYTES_PER_MB = 1024 * 1024; // Binary megabyte — matches typical OS/storage wording
const BYTES_PER_GB = 1024 * 1024 * 1024; // Binary gigabyte

/** Picks MB vs GB from the limit so used and limit share one scale (clear "of" / "/" pairs). */
function storageUsedAndLimitLabels(usedBytes: number, limitBytes: number): { used: string; limit: string } {
  const used = Math.max(0, usedBytes); // Clamp for display when API sends negatives
  const lim = Math.max(0, limitBytes);
  const useGb = lim >= BYTES_PER_GB; // Large quotas → GB; smaller → MB (avoids 0.0 GB noise)
  const scale = useGb ? BYTES_PER_GB : BYTES_PER_MB;
  const unit = useGb ? 'GB' : 'MB';
  return {
    used: `${(used / scale).toFixed(1)} ${unit}`, // One decimal for both sides
    limit: `${(lim / scale).toFixed(1)} ${unit}`,
  };
}

/** Single-byte label in GB only (binary); kept for callers that need one number. */
export function formatSubscriptionBytesToGbLabel(bytes: number): string {
  const b = Math.max(0, bytes);
  return `${(b / BYTES_PER_GB).toFixed(1)} GB`;
}

/**
 * Renders used/limit storage: "47.2 MB / 50.0 MB" (adaptive unit from limit).
 * Use in Admin Hub and anywhere a slash-separated pair is preferred.
 */
export function formatSubscriptionStorageGbPair(usedBytes: number, limitBytes: number): string {
  const { used, limit } = storageUsedAndLimitLabels(usedBytes, limitBytes); // Same scale for both
  return `${used} / ${limit}`;
}

/**
 * Same numbers as formatSubscriptionStorageGbPair but "used of total" like Channels/Messages.
 * Example: "47.2 MB of 50.0 MB"
 */
export function formatSubscriptionStorageUsedOfLimit(usedBytes: number, limitBytes: number): string {
  const { used, limit } = storageUsedAndLimitLabels(usedBytes, limitBytes);
  return `${used} of ${limit}`;
}
